import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Briefcase, GraduationCap, MapPin, MessageSquare, ArrowRight, CheckCircle2 } from 'lucide-react'
import { Avatar } from '../shared/Avatar'
import { VerifiedBadge } from '../shared/VerifiedBadge'
import { colors, radii, typography } from '../../styles/tokens'
import type { AlumniProfileItem } from '../../api/alumni'

interface AlumniCardProps {
  alumnus: AlumniProfileItem
  onAskQuestion?: (alumnus: AlumniProfileItem) => void
}

export function AlumniCard({ alumnus, onAskQuestion }: AlumniCardProps) {
  const navigate = useNavigate()

  return (
    <div
      style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 20,
        padding: '18px',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
        transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
        cursor: 'default',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--color-border-hover, rgba(17,17,20,0.22))'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--color-border)'
      }}
    >
      {/* Top Header: Avatar + Identity */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
        <div
          onClick={() => navigate(`/app/alumni/profile/${alumnus.user_id}`)}
          style={{ cursor: 'pointer', flexShrink: 0 }}
        >
          <Avatar
            src={alumnus.avatar_url}
            name={alumnus.full_name}
            size={54}
          />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <span
              onClick={() => navigate(`/app/alumni/profile/${alumnus.user_id}`)}
              style={{
                fontSize: '16px',
                fontWeight: 700,
                color: 'var(--color-text-primary)',
                fontFamily: typography.fontDisplay,
                cursor: 'pointer',
              }}
            >
              {alumnus.full_name}
            </span>
            {alumnus.is_verified === 1 && (
              <span title="Verified Alumnus" style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                <VerifiedBadge size={15} />
                <span
                  style={{
                    fontSize: '10px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.4px',
                    padding: '1px 6px',
                    borderRadius: 999,
                    background: 'var(--color-text-primary)',
                    color: 'var(--color-bg)',
                  }}
                >
                  Verified Alumni
                </span>
              </span>
            )}
            {alumnus.is_demo === 1 && (
              <span
                style={{
                  fontSize: '10px',
                  fontWeight: 600,
                  color: 'var(--color-text-muted)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '4px',
                  padding: '1px 5px',
                }}
              >
                Demo
              </span>
            )}
          </div>

          {/* Job title & Company */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginTop: '3px',
              fontSize: '13.5px',
              fontWeight: 600,
              color: 'var(--color-text-primary)',
            }}
          >
            <Briefcase size={14} style={{ color: 'var(--color-text-secondary)', flexShrink: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {alumnus.job_title} · {alumnus.company}
            </span>
          </div>

          {/* College, Degree, Grad Year */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginTop: '4px',
              fontSize: '12.5px',
              color: 'var(--color-text-secondary)',
            }}
          >
            <GraduationCap size={14} style={{ flexShrink: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {alumnus.previous_college} · Class of '{String(alumnus.graduation_year).slice(-2)} ({alumnus.degree_course})
            </span>
          </div>
        </div>
      </div>

      {/* Bio */}
      {alumnus.bio && (
        <p
          style={{
            margin: 0,
            fontSize: '13px',
            lineHeight: 1.55,
            color: 'var(--color-text-secondary)',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {alumnus.bio}
        </p>
      )}

      {/* Skills / Expertise chips */}
      {alumnus.skills && alumnus.skills.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {alumnus.skills.slice(0, 4).map((skill, idx) => (
            <span
              key={idx}
              style={{
                fontSize: '11.5px',
                fontWeight: 500,
                color: 'var(--color-text-secondary)',
                background: 'var(--color-bg)',
                border: '1px solid var(--color-border)',
                padding: '3px 9px',
                borderRadius: radii.pill,
              }}
            >
              {skill}
            </span>
          ))}
          {alumnus.skills.length > 4 && (
            <span
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: 'var(--color-text-muted)',
                padding: '3px 6px',
                alignSelf: 'center',
              }}
            >
              +{alumnus.skills.length - 4} more
            </span>
          )}
        </div>
      )}

      {/* Stats summary & Actions */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingTop: '10px',
          borderTop: '1px solid var(--color-border)',
          marginTop: 'auto',
          gap: '10px',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '12px', color: 'var(--color-text-muted)' }}>
          {alumnus.questions_answered_count !== undefined && (
            <span>
              <strong style={{ color: 'var(--color-text-primary)' }}>{alumnus.questions_answered_count}</strong> {alumnus.questions_answered_count === 1 ? 'answer' : 'answers'}
            </span>
          )}
          {alumnus.helpful_votes_count !== undefined && alumnus.helpful_votes_count > 0 && (
            <span>
              <strong style={{ color: 'var(--color-text-primary)' }}>{alumnus.helpful_votes_count}</strong> helpful votes
            </span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
          <button
            onClick={() => navigate(`/app/alumni/profile/${alumnus.user_id}`)}
            style={{
              padding: '7px 14px',
              borderRadius: radii.pill,
              border: '1px solid var(--color-border)',
              background: 'transparent',
              color: 'var(--color-text-primary)',
              fontSize: '12.5px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              transition: 'background 150ms ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-bg)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            View Profile
          </button>

          <button
            onClick={() => onAskQuestion ? onAskQuestion(alumnus) : navigate(`/app/alumni?ask=1&alumnus=${alumnus.user_id}`)}
            style={{
              padding: '7px 14px',
              borderRadius: radii.pill,
              border: 'none',
              background: '#111114',
              color: '#FFFFFF',
              fontSize: '12.5px',
              fontWeight: 700,
              fontFamily: typography.fontDisplay,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
            }}
          >
            <MessageSquare size={13} />
            Ask Question
          </button>
        </div>
      </div>
    </div>
  )
}
